import { Button, Icon, Modal } from '@octano/global-ui';
import { Trans, useTranslation } from 'react-i18next';

import { CONTACT_EMAIL } from '../../config/constants';
import { removeJWT } from '../../utils/auth';
import { MicrosoftErrorCode } from './parseMicrosoftError';

interface Props {
  isOpen: boolean;
  errorCode: MicrosoftErrorCode;
  email?: string;
  onClose: () => void;
}

/**
 * Modal genérico de error en el flujo SSO de Microsoft Entra ID. Cubre todos
 * los códigos excepto `NOT_SCOPE` (que tiene su propio modal con la opción
 * "Ir a Campus"). El botón principal limpia la sesión local y cierra el modal.
 */
export default function SsoErrorModal({
  isOpen,
  errorCode,
  email,
  onClose,
}: Props) {
  const { t } = useTranslation();

  const closeSession = () => {
    removeJWT();
    localStorage.removeItem('authProvider');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose}>
      <div className="d-flex flex-column align-items-center">
        <Icon name="warning" color="primary" size={50} />
        <h1 className="text-dark fs-20 mb-0 mt-4 text-center">
          {t('login.microsoftEntraId.error_modal.title')}
        </h1>

        {email ? (
          <div
            className="d-inline-flex align-items-center bg-light rounded-pill px-3 py-1 mt-3"
            style={{ gap: 8 }}
          >
            <Icon name="person_record" size={16} />
            <span className="fs-14 text-muted">{email}</span>
          </div>
        ) : null}

        <div
          className="w-100 mt-4 p-3 rounded d-flex align-items-start"
          style={{
            backgroundColor: '#FCE8E8',
            border: '1px solid #F5C2C2',
            gap: 12,
          }}
        >
          <Icon name="error" color="danger" size={20} />
          <p className="fs-14 mb-0 text-danger">
            <Trans
              i18nKey={`login.microsoftEntraId.error_modal.alerts.${errorCode}`}
              values={{ contactEmail: CONTACT_EMAIL }}
              components={{ strong: <strong /> }}
            />
          </p>
        </div>

        <Button
          outlined
          text={t('login.microsoftEntraId.error_modal.primary_action')}
          className="mt-4 w-100 text-uppercase"
          onClick={closeSession}
        />

        <a
          href={`mailto:${CONTACT_EMAIL}`}
          className="mt-3 fs-14 text-decoration-underline"
        >
          {t('login.microsoftEntraId.error_modal.contact_support')}
        </a>
      </div>
    </Modal>
  );
}
